import { log } from '../utils/logging.js';

class LanguageManager {
  #currentLang = 'en';
  #translations = {};

  constructor() {
    const saved = localStorage.getItem('selectedLanguage');
    const browserLang = navigator.language?.split('-')[0] || 'en';
    this.#currentLang = saved || (['en', 'fr', 'de'].includes(browserLang) ? browserLang : 'en');

    this.#loadTranslations(this.#currentLang).then(() => {
      if (document.readyState === 'loading') {
        window.addEventListener('DOMContentLoaded', () => this.updatePageText());
      } else {
        this.updatePageText();
      }
    });

    window.addEventListener('storage', e => {
      try {
        if (e.key === 'selectedLanguage' && e.newValue && e.newValue !== this.#currentLang) {
          this.setLanguage(e.newValue);
        }
      } catch (err) {
        console.warn('languageManager storage handler error', err);
      }
    });

    document.addEventListener('visibilitychange', () => {
      try {
        if (document.visibilityState === 'visible') {
          const saved = localStorage.getItem('selectedLanguage');
          if (saved && saved !== this.#currentLang) this.setLanguage(saved);
        }
      } catch (err) {
        console.warn('languageManager visibilitychange handler error', err);
      }
    });
  }

  async #loadTranslations(lang) {
    if (this.#translations[lang]) return;

    let module;
    switch (lang) {
      case 'fr':
        module = await import('./fr.js');
        break;
      case 'de':
        module = await import('./de.js');
        break;
      default:
        module = await import('./en.js');
        break;
    }

    this.#translations[lang] = module.default;
  }

  async setLanguage(lang) {
    this.#currentLang = lang;
    localStorage.setItem('selectedLanguage', lang);
    log('Language set to:', lang);
    await this.#loadTranslations(lang);
    this.updatePageText();
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: { lang } }));
  }

  getCurrentLanguage() {
    return this.#currentLang;
  }

  syncFromStorage() {
    try {
      const saved = localStorage.getItem('selectedLanguage');
      if (saved && saved !== this.#currentLang) this.setLanguage(saved);
    } catch (err) {
      console.warn('syncFromStorage failed', err);
    }
  }

  getText(key) {
    const val =
      this.#translations[this.#currentLang]?.[key] || this.#translations['en']?.[key] || key;
    if (Array.isArray(val)) return val; // return array as-is
    if (typeof val === 'object') return JSON.stringify(val);
    return val;
  }

  get(key) {
    return this.#translations[this.#currentLang]?.[key] || this.#translations['en']?.[key] || null;
  }

  updatePageText() {
    document.querySelectorAll('[data-i18n]').forEach(el => {
      const val = this.getText(el.getAttribute('data-i18n'));
      el.textContent = Array.isArray(val) ? val.join(', ') : val;
    });
    document.querySelectorAll('[data-i18n-html]').forEach(el => {
      const val = this.getText(el.getAttribute('data-i18n-html'));
      el.innerHTML = Array.isArray(val) ? val.join(', ') : val;
    });
    document.querySelectorAll('[data-i18n-title]').forEach(el => {
      const val = this.getText(el.getAttribute('data-i18n-title'));
      el.setAttribute('title', Array.isArray(val) ? val.join(', ') : val);
    });
    document.querySelectorAll('[data-i18n-list]').forEach(el => {
      const val = this.getText(el.getAttribute('data-i18n-list'));
      const items = Array.isArray(val) ? val : [val];
      el.innerHTML = items.map(item => `<li>${item}</li>`).join('');
    });
  }
}

export const langManager = new LanguageManager();
